import React, { useState, useEffect } from 'react';
import AuthenticatedImage from '../../../components/AuthenticatedImage';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';
import { 
  Table, Button, Badge, Form, Spinner, Modal, Card, Alert, Row, Col,
  Pagination, ListGroup, InputGroup
} from 'react-bootstrap';
import {
  Search,
  PersonPlus,
  PersonCheck,
  PeopleFill
} from 'react-bootstrap-icons';
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  Tooltip as MuiTooltip,
  Avatar
} from "@mui/material";
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API_BASE_URL from '../../../config/api';

/* ── Shared style tokens (matching Users page) ── */
const primaryGradient = "linear-gradient(135deg, #1a237e, #0d47a1)";
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
};
const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

/* ── Action icon button ── */
const ActionBtn = ({ tooltip, color, onClick, disabled, children }) => (
  <MuiTooltip title={tooltip} placement="top" arrow>
    <span>
      <Button
        variant="link"
        size="sm"
        onClick={onClick}
        disabled={disabled}
        className="p-1 rounded-2"
        style={{ color, lineHeight: 1, opacity: disabled ? 0.4 : 1, minWidth: 'auto' }}
      >
        {children}
      </Button>
    </span>
  </MuiTooltip>
);

// Helper: Find and return array of items with assigned status
const getPreselected = (items) =>
  items.filter(item => item.status === 'Assigned');

const AssignMentorTeam = () => {
  // State variables
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [locationList, setLocationList] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedInstructors, setSelectedInstructors] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAssignment, setLoadingAssignment] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalMentors, setTotalMentors] = useState(0);

  const getLocationString = (location) => {
    if (!location) return '-';
    return typeof location === 'object' ? location.location : location;
  };

  // Fetch mentors data with pagination and filters
  const fetchMentors = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      let statusFilter = '';
      if (filters.status === 'active') statusFilter = '1';
      else if (filters.status === 'blocked') statusFilter = '2';
      else if (filters.status === 'not verified') statusFilter = '0';
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: filters.search,
        filterLocation: filters.location,
        status: statusFilter
      };
      const res = await axios.get(`${API_BASE_URL}/api/admin/getRegister/mentor`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params
      });
      setMentors(res.data.users || []);
      setTotalMentors(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching mentors:', err);
      toast.error('Failed to load mentors');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API_BASE_URL}/api/location/admin/getAllLocations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      const locations = res.data?.data || [];
      const activeLocations = locations
        .filter(loc => loc.status === 'Active')
        .map(loc => ({
          id: loc._id,
          name: loc.location
        }));
      setLocationList(activeLocations);
    } catch (err) {
      console.error("Failed to fetch locations:", err);
      toast.error("Failed to load locations");
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    fetchMentors();
  }, [currentPage, itemsPerPage, filters]);

  useEffect(() => {
    fetchMentors();
    fetchLocations();
  }, []);

  // --- Modal fetch and selection logic ---

  // Fetch instructors by mentor/location, mark assigned as selected
  const fetchInstructorsByLocation = async (mentorId) => {
    try {
      setLoadingInstructors(true);
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${API_BASE_URL}/api/assignInstructor/getInstructorByLocation/${mentorId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setInstructors(res.data.instructors || []);
      // Preselect instructors with status 'Assigned'
      setSelectedInstructors(getPreselected(res.data.instructors || []));
    } catch (err) {
      console.error('Error fetching instructors:', err);
      toast.error('Failed to load instructors');
      setInstructors([]);
      setSelectedInstructors([]);
    } finally {
      setLoadingInstructors(false);
    }
  };

  // Fetch users by selected instructor(s), and mark assigned as selected
  const fetchUsersByInstructors = async (instructorIds) => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('adminToken');
      const usersPromises = instructorIds.map(async (instructorId) => {
        const res = await axios.get(
          `${API_BASE_URL}/api/assignInstructor/getUsersByInstructor/${instructorId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return res.data.users || [];
      });
      const usersArrays = await Promise.all(usersPromises);
      const combinedUsers = usersArrays.flat();
      // Only unique users by _id
      const uniqueUsers = combinedUsers.filter(
        (user, index, self) => index === self.findIndex(u => u._id === user._id)
      );

      setUsers(uniqueUsers);
      // Preselect users with status 'Assigned'
      setSelectedUsers(getPreselected(uniqueUsers));
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
      setSelectedUsers([]);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  // When selectedInstructors change, refetch their users and preselect
  useEffect(() => {
    if (selectedInstructors.length > 0) {
      fetchUsersByInstructors(selectedInstructors.map(i => i._id));
    } else {
      setUsers([]);
      setSelectedUsers([]);
    }
  }, [selectedInstructors]);

  // Filter users based on search term
  useEffect(() => {
    if (users.length > 0) {
      const filtered = users.filter(user =>
        user.name?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [users, userSearchTerm]);

  const clearSelections = () => {
    setSelectedInstructors([]);
    setSelectedUsers([]);
  };

  // Modal open will preselect based on data from backend
  const openAssignModal = (mentor) => {
    setSelectedUser(mentor);
    setUserSearchTerm('');
    setUsers([]);
    setSelectedUsers([]);
    setInstructors([]);
    setSelectedInstructors([]);
    fetchInstructorsByLocation(mentor._id);
    setShowAssignModal(true);
  };

  // Assign Team logic -- always send selected, even if empty for unassignment
  const handleAssignTeam = async () => {
    try {
      setLoadingAssignment(true);
      const token = localStorage.getItem('adminToken');
      const payload = {
        instructors: selectedInstructors.map(instructor => ({
          instructorId: instructor._id,
          userIds: selectedUsers.map(user => user._id)
        }))
      };
      await axios.post(
        `${API_BASE_URL}/api/assignInstructor/assignInstructorAndUsers/${selectedUser._id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Team assigned successfully!');
      setShowAssignModal(false);
      clearSelections();
    } catch (err) {
      console.error('Error assigning team:', err);
      toast.error(err.response?.data?.message || 'Failed to assign team');
    } finally {
      setLoadingAssignment(false);
    }
  };

  // Individual instructor selection toggle
  const toggleInstructorSelection = (instructor) => {
    setSelectedInstructors(prev => {
      const isSelected = prev.some(i => i._id === instructor._id);
      if (isSelected) {
        // Also remove from selected users users of this instructor
        return prev.filter(i => i._id !== instructor._id);
      } else {
        return [...prev, instructor];
      }
    });
  };

  // Individual user selection toggle
  const toggleUserSelection = (user) => {
    setSelectedUsers(prev => {
      const isSelected = prev.some(u => u._id === user._id);
      if (isSelected) {
        return prev.filter(u => u._id !== user._id);
      } else {
        return [...prev, user];
      }
    });
  };

  const totalPages = Math.ceil(totalMentors / itemsPerPage);

  return (
    <Box sx={{ p: 3.5, background: "#f0f4ff", minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2.5, fontSize: "0.8rem" }}>
        <Link underline="hover" color="inherit" href="/dashboard">
          Dashboard
        </Link>
        <Typography
          color="primary"
          sx={{ fontSize: "0.8rem", fontWeight: 600 }}
        >
          Assign Instructor
        </Typography>
      </Breadcrumbs>

      {/* Page header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "11px",
              background: primaryGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
            }}
          >
            <PeopleFill size={20} color="#fff" />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: "#1a237e", lineHeight: 1.2 }}
            >
                        Assign Instructor

            </Typography>
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Assign instructors and users to mentors
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          p: 2,
          mb: 2.5,
          border: "1px solid #e4eaf8",
          boxShadow: "0 1px 4px rgba(13,71,161,0.05)",
        }}
      >
        <Row className="g-2 align-items-center">
          <Col md={4}>
            <Box position="relative">
              <Search
                size={13}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#aab4cc",
                  zIndex: 1,
                }}
              />
              <Form.Control
                style={{ ...fieldStyle, paddingLeft: 30 }}
                placeholder="Search by name or email..."
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </Box>
          </Col>
          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
            >
              <option value="">All Locations</option>
              {locationList.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {loc.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Select
              style={fieldStyle}
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
              <option value="not verified">Not Verified</option>
            </Form.Select>
          </Col>
        </Row>
      </Box>

      {/* Mentors Table */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          border: "1px solid #e4eaf8",
          overflow: "hidden",
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)",
        }}
      >
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            gap={2}
            py={8}
          >
            <Spinner
              animation="border"
              style={{
                color: "#0d47a1",
                width: 36,
                height: 36,
                borderWidth: 3,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Loading mentors...
            </Typography>
          </Box>
        ) : (
          <Table hover className="mb-0 align-middle">
            <thead style={{ background: primaryGradient }}>
              <tr>
                {["#", "Profile", "Name", "Email", "Location", "Status", "Actions"].map((h) => (
                  <th
                    key={h}
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: 700,
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      padding: "13px 16px",
                      border: "none",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mentors.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-5 text-muted">
                    No mentors found.
                  </td>
                </tr>
              ) : (
                mentors.map((mentor, index) => (
                  <tr
                    key={mentor._id}
                    style={{ borderBottom: "1px solid #f0f4ff" }}
                  >
                    <td
                      style={{
                        color: "#aab4cc",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                      }}
                    >
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td>
                      <AuthenticatedImage
                        src={mentor.profilePicture || 'https://via.placeholder.com/40'}
                        alt="Profile"
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '2px solid #e4eaf8'
                        }}
                      />
                    </td>
                    <td style={{ fontWeight: 600, color: "#1a237e" }}>
                      {mentor.name || '-'}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {mentor.email || '-'}
                    </td>
                    <td style={{ fontSize: "0.82rem", color: "#6b7a99" }}>
                      {getLocationString(mentor.location)}
                    </td>
                    <td>
                      <span
                        className={`badge px-3 py-2 rounded-pill ${
                          mentor.accountStatus === 'active' ? 'bg-success' : 
                          mentor.accountStatus === 'blocked' ? 'bg-danger' : 
                          'bg-warning text-dark'
                        }`}
                        style={{ fontSize: '0.75rem' }}
                      >
                        {mentor.accountStatus === 'active' ? 'Active' : 
                         mentor.accountStatus === 'blocked' ? 'Blocked' : 
                         mentor.accountStatus === 'not verified' ? 'Not Verified' : 
                         mentor.accountStatus}
                      </span>
                    </td>
                    <td>
                      <ActionBtn
                        tooltip="Assign Team"
                        color="#0d47a1"
                        onClick={() => openAssignModal(mentor)}
                      >
                        <PersonPlus size={16} />
                      </ActionBtn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        {/* Pagination */}
        {totalMentors > 0 && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2.5}
            py={1.8}
            sx={{ borderTop: "1px solid #f0f4ff", background: "#fafbff" }}
          >
            <Typography variant="caption" sx={{ color: "#7b8ab8" }}>
              Showing{" "}
              <b style={{ color: "#1a237e" }}>
                {(currentPage - 1) * itemsPerPage + 1}
              </b>
              –
              <b style={{ color: "#1a237e" }}>
                {Math.min(currentPage * itemsPerPage, totalMentors)}
              </b>{" "}
              of <b style={{ color: "#1a237e" }}>{totalMentors}</b>
            </Typography>
            <Pagination size="sm" className="mb-0">
              <Pagination.First
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                style={{ border: 'none', background: 'transparent' }}
              />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ border: 'none', background: 'transparent' }}
              />
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Pagination.Item
                    key={pageNum}
                    active={pageNum === currentPage}
                    onClick={() => handlePageChange(pageNum)}
                    style={
                      pageNum === currentPage
                        ? {
                            backgroundColor: "#1a237e",
                            borderColor: "#1a237e",
                            color: "#fff",
                          }
                        : { border: 'none', background: 'transparent' }
                    }
                  >
                    {pageNum}
                  </Pagination.Item>
                );
              })}
              
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ border: 'none', background: 'transparent' }}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                style={{ border: 'none', background: 'transparent' }}
              />
            </Pagination>
          </Box>
        )}
      </Box>

      {/* Assign Team Modal */}
      <Modal 
        show={showAssignModal} 
        onHide={() => {
          setShowAssignModal(false);
          clearSelections();
        }}
        size="xl"
        centered
      >
        <Modal.Header
          closeButton
          className="text-white border-0"
          style={{ background: primaryGradient }}
        >
          <Modal.Title
            className="fw-bold d-flex align-items-center gap-2"
            style={{ fontSize: "0.95rem" }}
          >
            <PeopleFill size={18} /> Assign Team to {selectedUser?.name || 'Mentor'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f7f9ff", padding: '1.5rem' }}>
          <Row>
            {/* Instructors Column */}
            <Col md={5} className="pe-2">
              <Card className="h-100 shadow-sm" style={{ borderColor: '#e4eaf8' }}>
                <Card.Header
                  className="d-flex justify-content-between align-items-center text-white"
                  style={{ background: primaryGradient }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Available Instructors
                  </Typography>
                  {loadingInstructors && <Spinner animation="border" size="sm" variant="light" />}
                </Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
                  {instructors.length > 0 ? (
                    <ListGroup variant="flush">
                      {instructors.map(instructor => (
                        <ListGroup.Item
                          key={instructor._id}
                          action
                          active={selectedInstructors.some(i => i._id === instructor._id)}
                          onClick={() => toggleInstructorSelection(instructor)}
                          className="py-2 px-2"
                          style={{
                            backgroundColor: instructor.status === 'Assigned'
                              ? 'rgba(46, 125, 50, 0.08)'
                              : selectedInstructors.some(i => i._id === instructor._id)
                              ? 'rgba(13, 71, 161, 0.08)' : '#fff',
                            borderLeft: instructor.status === 'Assigned'
                              ? '4px solid #2e7d32'
                              : selectedInstructors.some(i => i._id === instructor._id)
                              ? '4px solid #0d47a1'
                              : 'none',
                            marginBottom: '2px',
                            borderRadius: '6px'
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div className="position-relative me-2">
                              <AuthenticatedImage
                                src={instructor.profilePicture || 'https://via.placeholder.com/40'}
                                alt="Profile"
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                  border: '2px solid #e4eaf8',
                                }}
                              />
                              {instructor.status === 'Assigned' && (
                                <span
                                  className="position-absolute top-0 start-100 translate-middle p-1 bg-success border border-light rounded-circle"
                                  style={{ width: '10px', height: '10px' }}
                                />
                              )}
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center">
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a237e' }}>
                                  {instructor.name}
                                </Typography>
                                <span
                                  className={`badge ${instructor.status === 'Assigned' ? 'bg-success' : 'bg-secondary'} rounded-pill`}
                                  style={{ fontSize: '0.6rem', padding: '3px 6px' }}
                                >
                                  {instructor.status || 'Unassigned'}
                                </span>
                              </div>
                              <Typography variant="caption" sx={{ color: '#6b7a99', display: 'block' }}>
                                {instructor.email}
                              </Typography>
                            </div>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <Alert variant="info" className="text-center m-2">
                      {loadingInstructors ? 'Loading instructors...' : 'No instructors available at this location'}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Users Column */}
            <Col md={7} className="ps-2">
              <Card className="h-100 shadow-sm" style={{ borderColor: '#e4eaf8' }}>
                <Card.Header
                  className="d-flex justify-content-between align-items-center text-white"
                  style={{ background: primaryGradient }}
                >
                  <div>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {selectedInstructors.length > 0 
                        ? `Users (${filteredUsers.length})` 
                        : 'Select Instructor(s)'}
                    </Typography>
                    {selectedInstructors.length > 0 && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Click to assign/unassign
                      </Typography>
                    )}
                  </div>
                  {loadingUsers && <Spinner animation="border" size="sm" variant="light" />}
                </Card.Header>
                <Card.Body style={{ maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
                  {selectedInstructors.length > 0 ? (
                    <>
                      <Box sx={{ mb: 2, px: 1 }}>
                        <InputGroup size="sm">
                          <InputGroup.Text style={{ background: '#f0f4ff', border: '1.5px solid #dde4f5' }}>
                            <Search size={12} color="#7b8ab8" />
                          </InputGroup.Text>
                          <Form.Control
                            style={fieldStyle}
                            placeholder="Search users..."
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                          />
                        </InputGroup>
                      </Box>
                      {filteredUsers.length > 0 ? (
                        <ListGroup variant="flush">
                          {filteredUsers.map(user => (
                            <ListGroup.Item
                              key={user._id}
                              action
                              active={selectedUsers.some(u => u._id === user._id)}
                              onClick={() => toggleUserSelection(user)}
                              className="py-2 px-2"
                              style={{
                                backgroundColor: user.status === 'Assigned'
                                  ? 'rgba(46, 125, 50, 0.08)'
                                  : selectedUsers.some(u => u._id === user._id)
                                  ? 'rgba(13, 71, 161, 0.08)'
                                  : '#fff',
                                borderLeft: user.status === 'Assigned'
                                  ? '4px solid #2e7d32'
                                  : selectedUsers.some(u => u._id === user._id)
                                  ? '4px solid #0d47a1'
                                  : 'none',
                                marginBottom: '2px',
                                borderRadius: '6px'
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div className="position-relative me-2">
                                  <AuthenticatedImage
                                    src={user.profilePicture || 'https://via.placeholder.com/40'}
                                    alt="Profile"
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '2px solid #e4eaf8'
                                    }}
                                  />
                                  {user.status === 'Assigned' && (
                                    <span
                                      className="position-absolute top-0 start-100 translate-middle p-1 bg-success border border-light rounded-circle"
                                      style={{ width: '8px', height: '8px' }}
                                    />
                                  )}
                                </div>
                                <div className="flex-grow-1">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#1a237e' }}>
                                      {user.name}
                                    </Typography>
                                    <span
                                      className={`badge ${user.status === 'Assigned' ? 'bg-success' : 'bg-secondary'} rounded-pill`}
                                      style={{ fontSize: '0.6rem', padding: '3px 6px' }}
                                    >
                                      {user.status || 'Unassigned'}
                                    </span>
                                  </div>
                                  <Typography variant="caption" sx={{ color: '#6b7a99', display: 'block' }}>
                                    {user.email}
                                  </Typography>
                                </div>
                              </div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <Alert variant="info" className="text-center m-2">
                          {loadingUsers ? 'Loading users...' : 'No users found matching your search'}
                        </Alert>
                      )}
                    </>
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      flexDirection="column"
                      py={5}
                    >
                      <PeopleFill size={40} style={{ color: '#aab4cc' }} />
                      <Typography variant="caption" sx={{ color: '#7b8ab8', mt: 2 }}>
                        Select instructors to view their users
                      </Typography>
                    </Box>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Selected Summary Section */}
          {(selectedInstructors.length > 0 || selectedUsers.length > 0) && (
            <Box sx={{ mt: 2 }}>
              {selectedInstructors.length > 0 && (
                <Box
                  sx={{
                    background: '#f0f4ff',
                    borderRadius: '8px',
                    p: 1.5,
                    mb: 1,
                    border: '1px solid #e4eaf8'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a237e', mb: 1, display: 'block' }}>
                    Selected Instructors ({selectedInstructors.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedInstructors.map(instructor => (
                      <span
                        key={instructor._id}
                        className="d-inline-flex align-items-center"
                        style={{
                          background: primaryGradient,
                          color: '#fff',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '0.7rem'
                        }}
                      >
                        <AuthenticatedImage
                          src={instructor.profilePicture || 'https://via.placeholder.com/20'}
                          alt="Profile"
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            marginRight: '6px'
                          }}
                        />
                        {instructor.name}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.3rem', opacity: 0.8 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleInstructorSelection(instructor);
                          }}
                        />
                      </span>
                    ))}
                  </Box>
                </Box>
              )}

              {selectedUsers.length > 0 && (
                <Box
                  sx={{
                    background: '#e8f5e9',
                    borderRadius: '8px',
                    p: 1.5,
                    border: '1px solid #c8e6c9'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1, display: 'block' }}>
                    Selected Users ({selectedUsers.length})
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {selectedUsers.map(user => (
                      <span
                        key={user._id}
                        className="d-inline-flex align-items-center"
                        style={{
                          background: '#2e7d32',
                          color: '#fff',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '0.7rem'
                        }}
                      >
                                         <AuthenticatedAvatar
                                                src={user.profilePicture}
                                                alt={user.name}
                                                sx={{
                                                  width: 40,
                                                  height: 40,
                                                  border: "2px solid #e4eaf8",
                                                }}
                                              />
                       
                        {user.name}
                        <button
                          type="button"
                          className="btn-close btn-close-white ms-2"
                          style={{ fontSize: '0.3rem', opacity: 0.8 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleUserSelection(user);
                          }}
                        />
                      </span>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: "#f7f9ff", borderTop: '1px solid #e4eaf8' }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowAssignModal(false);
              clearSelections();
            }}
            style={{ borderRadius: '8px' }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignTeam}
            disabled={loadingAssignment || (selectedInstructors.length === 0 && selectedUsers.length === 0)}
            className="d-flex align-items-center"
            style={{ background: primaryGradient, border: 'none', borderRadius: '8px' }}
          >
            {loadingAssignment ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Assigning...
              </>
            ) : (
              <>
                <PersonCheck className="me-2" size={16} />
                Assign Selected Team
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Box>
  );
};

export default AssignMentorTeam;
